import matter from 'gray-matter';
import { Buffer } from 'buffer';

// Polyfill for browser
if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
  window.Buffer = Buffer;
}

export interface TOCItem {
  id: string;
  title: string;
  level: number;
  children?: TOCItem[];
}

export interface SocialShareData {
  url: string;
  title: string;
  description: string;
  image: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  image: string;
  featured?: boolean;
  tableOfContents?: TOCItem[];
  socialShares?: SocialShareData;
  relatedTopics?: string[];
}

export interface BlogMeta {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  image: string;
  featured?: boolean;
}

// Simple reading time calculation for browser
function calculateReadingTime(text: string): string {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} min read`;
}

// Generate table of contents from markdown content
function generateTableOfContents(content: string): TOCItem[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const items: TOCItem[] = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length;
    const title = match[2].trim();
    const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    
    items.push({ id, title, level });
  }

  return items;
}

// Generate social sharing data
function generateSocialShareData(post: BlogPost, baseUrl: string = window.location.origin): SocialShareData {
  return {
    url: `${baseUrl}/blog/${post.slug}`,
    title: post.title,
    description: post.excerpt,
    image: post.image,
  };
}

// Extract related topics from content
function extractRelatedTopics(content: string, tags: string[]): string[] {
  const topics = new Set(tags);
  
  // Add common tech terms found in content
  const techTerms = [
    'AI', 'Machine Learning', 'Analytics', 'Dashboard', 'PowerBI', 'Shopify', 
    'E-commerce', 'Conversion', 'Optimization', 'Real-time', 'Data', 'Business Intelligence'
  ];
  
  techTerms.forEach(term => {
    if (content.toLowerCase().includes(term.toLowerCase())) {
      topics.add(term);
    }
  });
  
  return Array.from(topics).slice(0, 8); // Limit to 8 topics
}

class BlogService {
  private posts: BlogPost[] = [];
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) return;
    
    try {
      const modules = import.meta.glob('/content/blog/*.md', { eager: true, query: '?raw', import: 'default' });
      
      this.posts = Object.entries(modules).map(([path, content]) => {
        const slug = path.split('/').pop()?.replace('.md', '') || '';
        const { data, content: markdownContent } = matter(content as string);
        const readTime = calculateReadingTime(markdownContent);
        
        const post: BlogPost = {
          slug,
          title: data.title || 'Untitled',
          excerpt: data.excerpt || '',
          content: markdownContent,
          author: data.author || 'Anonymous',
          date: data.date || new Date().toISOString(),
          readTime,
          category: data.category || 'Uncategorized',
          tags: data.tags || [],
          image: data.image || 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&h=400',
          featured: data.featured || false,
          tableOfContents: generateTableOfContents(markdownContent),
          relatedTopics: extractRelatedTopics(markdownContent, data.tags || []),
        };

        // Add social share data when we have access to window
        if (typeof window !== 'undefined') {
          post.socialShares = generateSocialShareData(post);
        }

        return post;
      });
      
      this.posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      this.initialized = true;
    } catch (error) {
      console.error('Error loading blog posts:', error);
      this.posts = [];
    }
  }

  async getAllPosts(): Promise<BlogMeta[]> {
    await this.initialize();
    return this.posts.map(({ content, ...meta }) => meta);
  }

  async getPostBySlug(slug: string): Promise<BlogPost | null> {
    await this.initialize();
    return this.posts.find(post => post.slug === slug) || null;
  }

  async getPostsByCategory(category: string): Promise<BlogMeta[]> {
    await this.initialize();
    if (category === 'All') {
      return this.getAllPosts();
    }
    return this.posts
      .filter(post => post.category === category)
      .map(({ content, ...meta }) => meta);
  }

  async searchPosts(query: string): Promise<BlogMeta[]> {
    await this.initialize();
    const lowercaseQuery = query.toLowerCase();
    return this.posts
      .filter(post => 
        post.title.toLowerCase().includes(lowercaseQuery) ||
        post.excerpt.toLowerCase().includes(lowercaseQuery) ||
        post.content.toLowerCase().includes(lowercaseQuery) ||
        post.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
      )
      .map(({ content, ...meta }) => meta);
  }

  async getFeaturedPost(): Promise<BlogMeta | null> {
    await this.initialize();
    const featured = this.posts.find(post => post.featured);
    if (featured) {
      const { content, ...meta } = featured;
      return meta;
    }
    if (this.posts.length > 0) {
      const { content, ...meta } = this.posts[0];
      return meta;
    }
    return null;
  }

  async getRelatedPosts(slug: string, limit: number = 3): Promise<BlogMeta[]> {
    await this.initialize();
    const currentPost = this.posts.find(post => post.slug === slug);
    if (!currentPost) return [];

    // Enhanced related posts algorithm
    const scoredPosts = this.posts
      .filter(post => post.slug !== slug)
      .map(post => {
        let score = 0;
        
        // Same category gets high score
        if (post.category === currentPost.category) {
          score += 10;
        }
        
        // Shared tags get medium score
        const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
        score += sharedTags.length * 5;
        
        // Related topics get low score
        if (currentPost.relatedTopics && post.relatedTopics) {
          const sharedTopics = post.relatedTopics.filter(topic => 
            currentPost.relatedTopics!.includes(topic)
          );
          score += sharedTopics.length * 2;
        }
        
        return { post, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(({ post }) => post);

    return scoredPosts.map(({ content, tableOfContents, socialShares, relatedTopics, ...meta }) => meta);
  }

  getCategories(): string[] {
    const categories = new Set(['All']);
    this.posts.forEach(post => categories.add(post.category));
    return Array.from(categories);
  }

  // Get table of contents for a specific post
  async getTableOfContents(slug: string): Promise<TOCItem[]> {
    await this.initialize();
    const post = this.posts.find(p => p.slug === slug);
    return post?.tableOfContents || [];
  }

  // Get social sharing data for a post
  getSocialShareData(post: BlogPost): SocialShareData {
    if (post.socialShares) {
      return post.socialShares;
    }
    return generateSocialShareData(post);
  }

  // Get posts by related topics
  async getPostsByTopic(topic: string, limit: number = 5): Promise<BlogMeta[]> {
    await this.initialize();
    return this.posts
      .filter(post => 
        post.relatedTopics?.includes(topic) || 
        post.tags.includes(topic) ||
        post.category.toLowerCase() === topic.toLowerCase()
      )
      .slice(0, limit)
      .map(({ content, tableOfContents, socialShares, relatedTopics, ...meta }) => meta);
  }

  // Get all unique topics across all posts
  getAllTopics(): string[] {
    const topics = new Set<string>();
    this.posts.forEach(post => {
      post.tags.forEach(tag => topics.add(tag));
      post.relatedTopics?.forEach(topic => topics.add(topic));
      topics.add(post.category);
    });
    return Array.from(topics).sort();
  }
}

export const blogService = new BlogService();